import { ElementRef, inject, Injectable } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AppService } from '@app/app.service';
import { AppHelper } from '@app/core/app.helper';
import { Validator } from '@app/modules/dynamic-fields/field.interface';
import { LANG_DICTIONARY_TOKEN } from '@constants/diConstant';
import { LangService } from './lang.service';

/**
 * Сервис вспомогательных инструментов по работе с формами
 */
@Injectable()
export class FormToolsService {
    private readonly dictionary = inject(LANG_DICTIONARY_TOKEN);
    private readonly appService = inject(AppService);

    /**
     * Список сообщений по-умолчанию для распространённых валидаторов
     */
    defaultValidators: { [key: string]: Validator } = {
        required: {
            name: 'required',
            message: this.dictionary?.system_field_required,
        },
        serverError: {
            name: 'serverError',
        },
        positiveNumber: {
            name: 'min',
            message: this.dictionary?.factor_input_positive_number,
        },
    };

    /**
     * Создание мультиязычного поля
     * Обёртка для AppHelper.createLangField
     * @param formBuilder создатель форм
     * @param form форма
     * @param field поле
     * @param data данные
     * @param required обязательность поля
     */
    createLangField(formBuilder: FormBuilder, form: FormGroup, field: string, data: any, required: boolean) {
        AppHelper.createLangField(this.appService, formBuilder, form, field, data, required);
    }

    /**
     * Фокусировать первое невалидное поле на форме
     *
     * @param parentRef ElementRef для формы или другого родительского элемента, содержащего поля формы
     */
    focusFirstInvalid(parentRef: ElementRef) {
        const matches = [
            '.mat-select.ng-invalid',
            '.mat-input-element.ng-invalid',
            '.mat-checkbox-input.ng-invalid',
            'fui-text-input fui-text-bare input.ng-invalid',
            'fui-select:has(input.ng-invalid) fui-button button:has(fui-icon.mdi-chevron-down)',
        ];
        const invalidControl = parentRef.nativeElement.querySelector(matches.join(','));
        if (invalidControl) {
            setTimeout(() => {
                invalidControl?.scrollIntoView({ block: 'center' });
                invalidControl?.focus();
            });
        }
    }

    /**
     * Фокусировать нужное мультиязычное поле в группе мультиязычных полей, для которой не прошла валидация
     */
    focusLangField(langService: LangService, fieldGroup: FormGroup, fieldName: string) {
        if (fieldGroup.get(fieldName).invalid) {
            for (const lang of langService.languageNames) {
                if (fieldGroup.get(fieldName + '.' + lang).invalid) {
                    langService.currentLanguage = lang;
                    break;
                }
            }
        }
    }

    /**
     * Проставление для всех полей формы признака, что они не были отредактированы
     * @param formGroup объект формы
     */
    markAllAsPristine(formGroup: FormGroup) {
        if (formGroup && formGroup.controls && Object.keys(formGroup.controls).length) {
            Object.keys(formGroup.controls).forEach((field) => {
                const control = formGroup.get(field) as FormGroup;
                if (control && 'controls' in control && control.controls && Object.keys(control.controls).length) {
                    this.markAllAsPristine(control);
                } else {
                    if (control) {
                        control.markAsPristine();
                    }
                }
            });
        }
    }

    /**
     * Проставление для всех полей формы признака изменения
     * Обёртка для AppHelper.validateAllFormFields
     * @param formGroup объект формы
     */
    validateAllFormFields(formGroup: FormGroup) {
        AppHelper.validateAllFormFields(formGroup);
    }

    /**
     * Проставление валидации в поле
     * Обёртка для AppHelper.setErrorField
     * @param property поле формы
     * @param data данные по ошибке
     */
    setErrorField(property: any, data: any) {
        AppHelper.setErrorField(property, data);
    }

    /**
     * Возвращает конфигурацию группы мультиязычных полей
     */
    createLangGroup<T>(name: string, initialValue: T, required: boolean = false, validators: any[] = []): any {
        const result = {};
        this.appService.languageNames.forEach((lang) => {
            result[lang] = [
                initialValue && initialValue[lang] ? initialValue[lang] : '',
                [...(required && lang === this.appService.defaultLanguage ? [Validators.required] : []), ...validators],
            ];
        });
        return result;
    }
}
